// src/pages/PaiementRetour.js
import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import SEO from '../components/SEO';
import { useTranslation } from 'react-i18next';

// Statuts Mollie qui indiquent que le paiement n'a pas abouti
const FAILED_STATUSES = ['failed', 'expired', 'canceled'];

const styles = {
  container: {
    minHeight: '60vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px'
  },
  card: {
    maxWidth: 480,
    width: '100%',
    textAlign: 'center',
    background: '#fff',
    borderRadius: 16,
    padding: '48px 32px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)'
  },
  spinner: {
    width: 48,
    height: 48,
    margin: '0 auto 24px',
    border: '4px solid #eee',
    borderTopColor: '#7C3AED',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  icon: {
    width: 64,
    height: 64,
    margin: '0 auto 24px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 32,
    fontWeight: 'bold',
    color: '#fff'
  },
  iconSuccess: { background: '#22c55e' },
  iconError: { background: '#ef4444' },
  notice: { color: '#b45309', fontSize: 14, marginTop: 12 },
  button: {
    display: 'inline-block',
    marginTop: 24,
    padding: '12px 28px',
    background: '#000',
    color: '#fff',
    borderRadius: 8,
    textDecoration: 'none',
    fontWeight: 600
  }
};

function PaiementRetour() {
  const { t } = useTranslation('paiementRetour');
  const [searchParams] = useSearchParams();
  const paymentId = searchParams.get('paymentId');

  const [status, setStatus] = useState('loading'); // loading | paid | failed | pending | notfound
  const [slowNotice, setSlowNotice] = useState(false);

  useEffect(() => {
    if (!paymentId) {
      setStatus('notfound');
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'payments', paymentId),
      (snapshot) => {
        if (!snapshot.exists()) {
          setStatus('notfound');
          return;
        }
        const data = snapshot.data();
        if (data.status === 'paid') {
          setStatus('paid');
        } else if (FAILED_STATUSES.includes(data.status)) {
          setStatus('failed');
        } else {
          setStatus('pending');
        }
      },
      () => setStatus('notfound')
    );

    return () => unsubscribe();
  }, [paymentId]);

  // Si ça reste "pending" trop longtemps, on informe l'utilisateur (le webhook a pu être retardé)
  useEffect(() => {
    if (status !== 'pending' && status !== 'loading') return;
    const timer = setTimeout(() => setSlowNotice(true), 15000);
    return () => clearTimeout(timer);
  }, [status]);

  return (
    <div style={styles.container}>
      <SEO title={t('seo.title')} description={t('seo.description')} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <div style={styles.card}>
        {(status === 'loading' || status === 'pending') && (
          <>
            <div style={styles.spinner} />
            <h1>{t('pending.title')}</h1>
            <p>{t('pending.message')}</p>
            {slowNotice && <p style={styles.notice}>{t('pending.slowNotice')}</p>}
          </>
        )}

        {status === 'paid' && (
          <>
            <div style={{ ...styles.icon, ...styles.iconSuccess }}>✓</div>
            <h1>{t('success.title')}</h1>
            <p>{t('success.message')}</p>
            <Link to="/" style={styles.button}>{t('backHome')}</Link>
          </>
        )}

        {status === 'failed' && (
          <>
            <div style={{ ...styles.icon, ...styles.iconError }}>✕</div>
            <h1>{t('failed.title')}</h1>
            <p>{t('failed.message')}</p>
            <Link to="/" style={styles.button}>{t('backHome')}</Link>
          </>
        )}

        {status === 'notfound' && (
          <>
            <div style={{ ...styles.icon, ...styles.iconError }}>?</div>
            <h1>{t('notfound.title')}</h1>
            <p>{t('notfound.message')}</p>
            <Link to="/" style={styles.button}>{t('backHome')}</Link>
          </>
        )}
      </div>
    </div>
  );
}

export default PaiementRetour;
